import { calcTimeDelta } from 'react-countdown'
import { formatDate } from 'components/CallToAction/utils'
import parseFeatures from 'utils/parseFeatures'
import parseExpenses from 'utils/parseExpenses'
import numberWithDots from 'utils/numberWithDots'
import handleTags from 'utils/tags'
import isConcurrenceProperty from 'utils/isConcurrenceProperty'
import {
  isAuctionCheck,
  isDirectOrAgencySale,
  isCompetition,
  isLearnMore,
  isHideDisplay,
  isClosed,
  isSold,
  isSaleValue,
  isReferenceValue,
  isUpFrontValue,
  paymentConditionSwitch,
  isSacre,
} from './utils'
import { labelsDictionary } from './dictionaries'

export default $property => {
  const getPropertyData = () => $property.data
  const getPropertyId = () => ($property.data ? $property.data.propertyId : '')
  const getPropertySmallId = () => $property.data.propertySmallId || ''
  const getLoadingProperty = () => $property.loadingProperty
  const getContactInfo = () => $property.data && $property.data.contactInfo
  const getPaymentConditions = () =>
    $property.data && $property.data.paymentConditions
  const getFinancingValue = () => ({
    minimumInput: $property.data ? $property.data.minimumInput : '',
    salePrice: $property.data ? parseInt($property.data.salePrice, 10) : '',
  })
  const getSaleType = () => {
    if (!$property.data) return null
    if ($property.data.isAuction) return 'auction'
    if ($property.data.tags.some(tag => tag === 'Venda direta')) return 'direct'
    if ($property.data.tags.some(tag => tag === 'Imobiliária'))
      return 'realestate'
    return null
  }

  return {
    getPropertyData,
    getPropertyId,
    getPropertySmallId,
    getContactInfo,
    getPaymentConditions,
    getLoadingProperty,
    getFinancingValue,
    getSaleType,
  }
}

/**
 * IN: some of the keys sent by API which are majorly related to property selling attributes and dates.
 * Treating these keys and assigning the proper value to CTA labels and CTA togglers (true, false)
 * @param {string} tipo_de_venda
 * @param {string} data_limite
 * @param {string} datas_leilao
 * @param {string} call_to_action_url
 * @param {string} call_to_actions
 * @param {string} status_da_venda
 * @returns
 * @author Leandro Parisi
 * @since 2021.04.09
 */
const setAuctionAndCTALabels = ({
  tipo_de_venda,
  data_limite,
  datas_leilao,
  call_to_action_url,
  call_to_actions,
  status_da_venda,
}) => {
  const tipoVenda = tipo_de_venda.toLowerCase()
  let callToActionLabel
  let showContactUs = false
  let contactButtonLabel
  let showAuctionDate = false
  let auctionInfo = []
  let callToActionUrl
  let isAuction = false
  let remainingCouncurrenceTime = false
  let isConcurrenceOver = false
  let isPublicCompetition = false
  if (isDirectOrAgencySale(tipoVenda)) {
    callToActionLabel = labelsDictionary.directSale.CTA
    showContactUs = true
    contactButtonLabel = labelsDictionary.directSale.CB
  } else if (isAuctionCheck(tipoVenda)) {
    if (data_limite && !datas_leilao) {
      showAuctionDate = true
      auctionInfo.push({
        data: data_limite.split(' ', 2)[0],
        hora: data_limite.split(' ', 2)[1],
        label: labelsDictionary.auction.info,
      })
    }
    if (datas_leilao) {
      showAuctionDate = true
      auctionInfo = Object.values(datas_leilao)
      auctionInfo.forEach((_, index) => {
        auctionInfo[index].label = `${index + 1}º Leilão`
      })
    }
    showContactUs = true
    callToActionLabel = labelsDictionary.auction.CTA
    callToActionUrl = call_to_action_url
    contactButtonLabel = labelsDictionary.auction.CB
    isAuction = true
  } else if (isCompetition(tipoVenda)) {
    showContactUs = true
    callToActionLabel = labelsDictionary.competition.CTA
    contactButtonLabel = labelsDictionary.competition.CB

    if (data_limite) {
      showAuctionDate = true
      auctionInfo.push({
        data: data_limite.split(' ', 2)[0],
        hora: data_limite.split(' ', 2)[1],
        label: labelsDictionary.competition.info,
      })

      remainingCouncurrenceTime =
        auctionInfo[0] &&
        calcTimeDelta(formatDate(auctionInfo[0].data, auctionInfo[0].hora))

      isConcurrenceOver =
        remainingCouncurrenceTime &&
        callToActionLabel === labelsDictionary.competition.CTA &&
        remainingCouncurrenceTime.completed

      if (isConcurrenceOver) {
        callToActionLabel = labelsDictionary.over.CTA
      }
    }

    isPublicCompetition = true
  }

  if (isLearnMore(call_to_actions)) {
    callToActionLabel = labelsDictionary.learnMore.CTA
  }

  if (isClosed(status_da_venda)) {
    callToActionLabel = labelsDictionary.over.CTA
    callToActionUrl = ''
  } else if (isSold(status_da_venda)) {
    callToActionLabel = labelsDictionary.sold.CTA
    callToActionUrl = ''
  }
  return {
    callToActionLabel,
    showContactUs,
    contactButtonLabel,
    showAuctionDate,
    auctionInfo,
    callToActionUrl,
    isAuction,
    isConcurrenceOver,
    isPublicCompetition,
  }
}

/**
 * IN: some of the keys sent by API which are majorly related to property value and payment conditions.
 * Treating these keys and assigning the proper values to mainValue and togglers (true, false)
 * @param {object} carteira
 * @param {object} valores
 * @param {array} callToActionConsiderations
 * @param {array} formas_pagamento_card
 * @param {boolean} isCaixa
 * @returns
 * @author Leandro Parisi
 * @since 2021.04.09
 */
const setValuesAndConsiderations = (
  carteira,
  valores,
  callToActionConsiderations,
  formas_pagamento_card
) => {
  let { valor_exibido } = carteira
  const callToActionConsiderationsIn = [...callToActionConsiderations]
  if (!valor_exibido) {
    valor_exibido = ''
  }

  const { condicoes } = carteira
  let mainValue
  let showDiscount = true
  let hideValue = false
  let showProposal = true
  let hideConditions = false

  // ocultar valores e descontos/condicoes de pagamento nos detalhes da pagina
  if (isHideDisplay(valor_exibido)) {
    hideConditions = true
  }
  if (isSaleValue(valor_exibido)) {
    mainValue = valores.valor_venda

    if (condicoes.a_vista) {
      const { desconto } = condicoes.a_vista
      if (desconto) {
        callToActionConsiderationsIn.unshift(
          `* Imóvel tem ${desconto}% de desconto para pagamento à vista;`
        )
      }
    }
  } else if (isUpFrontValue(valor_exibido)) {
    mainValue = valores.valor_a_vista

    if (valores.valor_a_prazo) {
      formas_pagamento_card.push({
        title: 'Valor a prazo',
        value: valores.valor_a_prazo,
      })
    }
  } else if (isReferenceValue(valor_exibido)) {
    mainValue = valores.valor_referencia
    callToActionConsiderationsIn.push(`*Valor mínimo para envio de propostas;`)
  } else if (isHideDisplay(valor_exibido)) {
    showDiscount = false
    hideValue = true
    showProposal = false
  }

  return {
    hideConditions,
    showDiscount,
    hideValue,
    showProposal,
    mainValue,
    callToActionConsiderationsIn,
  }
}

/**
 * IN: some of the keys sent by API which are majorly related to property payment conditions.
 * Treating these keys and assigning the proper values to formasPagamentoOut which will is an array of object with all information related to payment regime
 * @param {object} condicoes
 * @param {array} formas_pagamento_card
 * @returns
 * @author Leandro Parisi
 * @since 2021.04.09
 */
const setPaymentMethod = ({ condicoes, formas_pagamento_card }) => {
  const formsInstallment = [...formas_pagamento_card]
  let isDirectInstallment = false

  if (condicoes.parcelamento_direto) {
    isDirectInstallment = true

    formsInstallment.push({
      title: 'Parcelamento Direto',
      content: [
        {
          label: 'Entrada mínima',
          value: condicoes.parcelamento_direto.entrada_minima,
        },
      ],
    })

    const position = formsInstallment
      .map(element => element.title)
      .indexOf('Parcelamento Direto')

    if (isSacre(condicoes)) {
      formsInstallment[position].content.push({
        label: `Saldo em até 120x + Amortização SACRE + Correção Poupança + juros de 8% a.a.`,

        value: '',
      })
    } else if (condicoes.parcelamento_direto.simulacao) {
      const {
        valor_prestacao,
        indexador,
        prestacoes,
      } = condicoes.parcelamento_direto

      const indexText = indexador ? ` + ${indexador}` : ''

      formsInstallment[position].content.push({
        label: `Saldo em até ${prestacoes}x de `,
        value: `R$ ${numberWithDots(valor_prestacao)}${indexText}`,
      })
    } else {
      const {
        valor_prestacao,
        indexador,
        juros,
        prestacoes,
      } = condicoes.parcelamento_direto

      formsInstallment[position].content.push({
        label: `Saldo em ${prestacoes}x de R$ ${numberWithDots(
          valor_prestacao
        )} c/  juros de ${juros}% a.m.+ ${indexador}`,
        value: '',
      })
    }
  }
  return { formsInstallment, isDirectInstallment }
}

/**
 * IN: some of the keys sent by API which are majorly related to property payment conditions.
 * Treating these keys and assigning the proper values to formasPagamentoOut which will is an array of object with all information related to payment regime, and toggler (true, false)
 * This function works similar to setPaymentMethod but it's focused on information about financing options the buyer may have
 * @param {object} condicoes
 * @param {array} formas_pagamento_card
 * @param {boolean} isCaixa
 * @returns
 * @author Leandro Parisi
 * @since 2021.04.09
 */
const setFinancingConditions = (condicoes, formas_pagamento_card) => {
  const { financiamento_imobiliario } = condicoes
  let showFinanceEstate = false
  let financialText
  const formasPagamentoIn = [...formas_pagamento_card]

  if (financiamento_imobiliario) {
    showFinanceEstate = true
    formasPagamentoIn.push({
      title: 'Financiamento Imobiliário',
      content: [
        {
          label: 'Entrada mínima',
          value: condicoes.financiamento_imobiliario.entrada_minima,
        },
        {
          label: 'Saldo total',
          value: condicoes.financiamento_imobiliario.saldo_total,
        },
      ],
    })

    const { banco, escolha_banco } = financiamento_imobiliario

    financialText = paymentConditionSwitch(escolha_banco, banco)
  }
  return {
    showFinanceEstate,
    formasPagamentoIn,
    financialText,
  }
}

export const serializeProperty = data => {
  const {
    aceita_visita,
    caracteristicas,
    areas,
    despesas,
    valores,
    tipo_de_venda,
    data_limite,
    datas_leilao,
    call_to_action_url,
    call_to_actions,
    carteira,
    tags,
    status_da_venda,
    recebeu_oferta,
    conditions,
  } = data

  const { condicoes, tipo_comissao, quem_paga_comissao } = carteira

  // OUT - Variáveis 'Globais' - corretas:
  const newExpenses = parseExpenses(despesas)
  const newFeatures = parseFeatures(caracteristicas, areas)
  const movie = data.video ? data.video.replace('watch?v=', 'embed/') : null
  const showVisit = aceita_visita === 'sim'
  const salePrice = data.valores.valor_venda
  const saleLabel = isConcurrenceProperty(handleTags(tags))
    ? 'Proposta Mínima'
    : data.valores.valor_venda_label
  const mainValueLabel = isConcurrenceProperty(handleTags(tags))
    ? 'Proposta Mínima'
    : valores.valor_venda_label
  const valor_exibido = carteira.valor_exibido || ''

  let callToActionConsiderations = [`* ${data.consideracoes_extra}`]
  let formas_pagamento_card = []

  const {
    callToActionLabel,
    showContactUs,
    contactButtonLabel,
    showAuctionDate,
    auctionInfo,
    callToActionUrl,
    isAuction,
    isConcurrenceOver,
    isPublicCompetition,
  } = setAuctionAndCTALabels({
    tipo_de_venda,
    data_limite,
    datas_leilao,
    call_to_action_url,
    call_to_actions,
    status_da_venda,
  })

  const {
    hideConditions,
    showDiscount,
    hideValue,
    showProposal,
    mainValue,
    callToActionConsiderationsIn,
  } = setValuesAndConsiderations(
    carteira,
    valores,
    callToActionConsiderations,
    formas_pagamento_card
  )
  callToActionConsiderations = callToActionConsiderationsIn

  const { formsInstallment, isDirectInstallment } = setPaymentMethod({
    condicoes,
    formas_pagamento_card,
  })
  formas_pagamento_card = [...formsInstallment]

  const fullContactButton = showContactUs && !showVisit

  const {
    showFinanceEstate,
    formasPagamentoIn,
    financialText,
  } = setFinancingConditions(condicoes, formas_pagamento_card)
  formas_pagamento_card = [...formasPagamentoIn]

  //

  const tipoVenda = tipo_de_venda.toLowerCase()
  if (mainValueLabel === 'Lance mínimo' && !isAuctionCheck(tipoVenda)) {
    if (
      tipo_comissao.toLowerCase() === 'incorporada' &&
      quem_paga_comissao.toLowerCase() === 'comprador'
    ) {
      callToActionConsiderations.push(
        `*No valor do imóvel já estão embutidos os honorários a serem pagos pelo comprador`
      )
    }
  }

  if (valor_exibido.toLowerCase() === 'nao_exibir') {
    callToActionConsiderations = []
  }

  const property = {
    propertyId: data.id,
    propertySmallId: data.id_do_imovel,
    propertyName: data.imovel_name,
    address: data.endereco.endereco_completo,
    saleCategory: data.categoria_venda,
    campaignInfo: {
      values: {
        campaignValue: data.valores.valor_campanha,
        campaignTotalValue: data.valores.valor_total_campanha,
      },
      dates: {
        campaignStartDate: data.data_inicio_campanha,
        campaignEndData: data.data_fim_campanha,
      },
    },
    juridical: {
      updatedAt: data.juridico.atualizado_em,
      scorePoints: data.juridico.pontuacao_score,
      status: data.juridico.status_processo_principal,
      processStatus: data.juridico.status_processo_principal,
      contencioso: data.juridico.contencioso,
      judicialProcessTimeline: data.juridico.timeline_processo_judicial,
    },
    tags: handleTags(data.tags || []),
    description: data.descricao,
    considerations: data.consideracoes_importantes,
    appraisedPrice: data.valores.valor_avaliado,
    appraisedLabel: data.valores.valor_avaliado_label,
    minimumInput:
      condicoes &&
      condicoes.financiamento_imobiliario &&
      condicoes.financiamento_imobiliario.entrada_minima
        ? condicoes.financiamento_imobiliario.entrada_minima
        : '',
    salePrice, // usa no card
    saleLabel,
    mainValue: Math.round(mainValue), // card
    mainValueLabel, // card
    forwardPrice: data.valores.valor_a_prazo,
    cashPrice: data.valores.valor_a_vista,
    commissionAmount:
      data.carteira.taxa_comissao == null
        ? 0
        : data.carteira.taxa_comissao / 100,
    expenses: newExpenses,
    isSacre: isSacre(data.carteira.condicoes),
    debts: data.dividas,
    stage: data.estagio_retorno,
    images: data.imagens.map((img, index) => ({
      url: img.url,
      alt: `imagem ${index + 1} do imóvel ${data.imovel_name}`,
    })),
    location: `${data.mapa.lat}, ${data.mapa.long}`,
    paymentConditions: data.formas_pagamento,
    registration: data.matricula,
    rgi: data.rgi,
    cityHallRegistration: data.inscricao_prefeitura,
    bankId: data.id_banco,
    features: newFeatures,
    contactInfo: data.canal_atendimento,
    movie,
    documents: data.documents,
    callToActionConsiderations, // card
    showDiscount,
    hideValue,
    desagio: data.desagio,
    showProposal, // card
    showVisit, // card
    showContactUs, // card
    financialText,
    fullContactButton,
    contactButtonLabel,
    callToActionLabel,
    callToActionUrl,
    showFinanceEstate,
    isDirectInstallment,
    showAuctionDate,
    auctionInfo,
    isConcurrenceOver,
    hideConditions,
    formas_pagamento_card,
    salesman: data.vendedor,
    map: data.mapa,
    isAuction,
    isPublicCompetition,
    acceptLow:
      condicoes &&
      condicoes.financiamento_imobiliario &&
      condicoes.financiamento_imobiliario.aceita_abaixo
        ? data.carteira.condicoes.financiamento_imobiliario.aceita_abaixo ===
          'sim'
        : false,
    externalTour: data.url_tour_externo || '',
    internalTour: data.url_tour_interno || '',
    offerReceived: recebeu_oferta,
    conditions,
    category: data.tipo_imovel,
    videos: data.videos,
    document: data.documento_regulamentacao,
  }
  return property
}
